// src/components/parcours/renderers/specialized/ImageComparator.jsx
import { MoveHorizontal } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:5000';

const ImageComparator = ({ images = [], mode = 'slider' }) => {
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  // Helper pour résoudre les URLs selon ta logique
  const resolveImg = (img) => {
    if (!img) return '';
    if (img.startsWith('http')) return img;
    // On nettoie le chemin pour éviter les doubles slashs
    const cleanPath = img.replace(/^\/+/, '').replace(/^uploads\//, '').replace(/^images\//, '');
    return `${API_URL}/images/${cleanPath}`;
  };

  const handleMove = (e) => {
    if (!isDragging && e.type !== 'click') return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.pageX || e.touches?.[0].pageX) - rect.left;
    const position = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPos(position);
  };

  // Empêcher le scroll quand on drag sur mobile
  useEffect(() => {
    const preventDefault = (e) => isDragging && e.preventDefault();
    document.addEventListener('touchmove', preventDefault, { passive: false });
    return () => document.removeEventListener('touchmove', preventDefault);
  }, [isDragging]);

  if (!images || images.length === 0) return null;

  // MODE SIDE-BY-SIDE (Si plus de 2 images ou mode forcé)
  if (mode === 'gallery' || images.length > 2) {
    return (
      <div className="grid grid-cols-2 gap-4 h-full p-4">
        {images.map((img, idx) => (
          <div key={idx} className="relative group rounded-2xl overflow-hidden bg-black/40 border border-white/5">
            <img 
              src={resolveImg(img)} 
              alt={`Analysis ${idx}`} 
              className="w-full h-full object-contain"
            />
            <div className="absolute top-4 left-4 px-3 py-1 bg-black/60 backdrop-blur-md rounded-lg text-[10px] font-mono text-cyan-400 border border-cyan-500/20 uppercase">
              Capture_0{idx + 1}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // MODE SLIDER (Avant / Après)
  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full overflow-hidden select-none touch-none bg-[#010204] cursor-ew-resize"
      onMouseMove={handleMove}
      onMouseDown={() => setIsDragging(true)}
      onMouseUp={() => setIsDragging(false)}
      onMouseLeave={() => setIsDragging(false)}
      onTouchMove={handleMove}
      onTouchStart={() => setIsDragging(true)}
      onTouchEnd={() => setIsDragging(false)}
    >
      {/* Image de Fond (Après / Right) */}
      <img 
        src={resolveImg(images[1] || images[0])} 
        alt="After"
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
      />

      {/* Image de Premier Plan (Avant / Left) */}
      <div 
        className="absolute inset-y-0 left-0 overflow-hidden border-r-2 border-cyan-500 shadow-[10px_0_40px_rgba(0,255,255,0.2)]"
        style={{ width: `${sliderPos}%` }}
      >
        <img 
          src={resolveImg(images[0])} 
          alt="Before"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          style={{ width: containerRef.current?.offsetWidth }}
        />
      </div>

      {/* Curseur Central */}
      <div 
        className="absolute inset-y-0 z-30 pointer-events-none"
        style={{ left: `${sliderPos}%` }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-cyan-500 rounded-full shadow-[0_0_30px_rgba(34,211,238,0.6)] flex items-center justify-center border-4 border-[#010204]">
          <MoveHorizontal size={20} className="text-black" />
        </div>
        
        {/* Labels Flottants */}
        <div className="absolute top-6 right-6 px-3 py-1 bg-black/50 backdrop-blur-sm rounded text-[9px] font-mono text-white/50 uppercase tracking-widest border border-white/10">
          État_Final
        </div>
        <div className="absolute top-6 left-[-100px] px-3 py-1 bg-cyan-500/20 backdrop-blur-sm rounded text-[9px] font-mono text-cyan-400 uppercase tracking-widest border border-cyan-500/30">
          État_Initial
        </div>
      </div>
    </div>
  );
};

export default ImageComparator;